import { useEnum } from "@/api/services/global/enum";
import { DATE_FORMAT } from "@/constants/datetime";
import { JobStatusTag } from "@/features/job/components/JobStatusTag";
import { JobTagList } from "@/features/job/components/JobTagList";
import { Tag } from "@/components/elements/Tag";
import { text } from "@/theme/colors";
import { FCC } from "@/types/common";
import { findLabelByValue } from "@/utils/object";
import { Box, Grid, Menu, MenuItem, ListItemText, ListItemIcon, Stack, styled, Tooltip, Typography } from "@mui/material";
import { JOB_WIDTH_FULL } from "@/features/job/constants/width";
import { JOB_EXPORT_FAILED_MESSAGE } from "@/features/job/constants/common";
import dayjs from "dayjs";
import { useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/elements/Button";
import { IconSocialShare, IconInfoOutline, IconChevronDown, IconAssignment } from "@/assets/icons";
import { ShareEnterpriseURLModal } from "@/components/elements/Modal/ShareEnterpriseURLModal";
import { useVisible } from "@/hooks/useVisible";
import { SHARE_TYPE } from "@/api/services/main/sharing/dtos/sharing.dto";
import { TJobShareLinks } from "@/features/job/types/JobForm";
import { exportJobDocxApi } from "@/api/services/main/job";
import { toast } from "react-toastify";
import { JobExportOptionsModal } from "@/features/job/components/JobExportOptionsModal";

type JobTitleProps = {
  title?: string | null | undefined;
  tagIds?: string[] | null | undefined;
  status?: string | null | undefined;
  createdAt?: string | null | undefined;
  updatedAt?: string | null | undefined;
  shareLinks?: TJobShareLinks | null;
  onShareGenerated?: () => void;
  showShareButton?: boolean;
  isCompanyArchived?: boolean;
};

export const JobTitle: FCC<JobTitleProps> = ({ title, tagIds, status, createdAt, updatedAt, shareLinks, onShareGenerated, showShareButton = false, isCompanyArchived }) => {
  const { jobId } = useParams();
  const { data: jobStatusOptions } = useEnum("EJobStatus");
  const jobTitle = title ? title : "新規求人作成";

  const shareModal = useVisible();
  const exportOptionsModal = useVisible();
  const [shareMenuAnchorEl, setShareMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [exportMenuAnchorEl, setExportMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [shareType, setShareType] = useState<"full" | "masked">("full");

  const currentShareLink = shareType === "full" ? shareLinks?.full : shareLinks?.masked;

  const handleShareMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setShareMenuAnchorEl(event.currentTarget);
  };

  const handleShareMenuClose = () => {
    setShareMenuAnchorEl(null);
  };

  const handleShareOption = (type: "full" | "masked") => {
    setShareType(type);
    handleShareMenuClose();
    shareModal.open();
  };

  const handleExportMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchorEl(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchorEl(null);
  };

  const [isExporting, setIsExporting] = useState(false);
  const handleExportDocx = useCallback(async () => {
    if (!jobId || isExporting) return;
    setIsExporting(true);
    try {
      const blob = await exportJobDocxApi(jobId);
      const fileName = `${title ?? "求人票"}の求人票.docx`;
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      toast.error(JOB_EXPORT_FAILED_MESSAGE);
    } finally {
      setIsExporting(false);
    }
  }, [jobId, title, isExporting]);

  const JobStatus = () => {
    if (isCompanyArchived) {
      return <Tag variant="outline" color={text.secondary} title="アーカイブ済み" />;
    }
    if (status) {
      return <JobStatusTag status={status} title={findLabelByValue(jobStatusOptions, status)} variant="bold" />;
    }
  };

  const reserveButtonSpace = Boolean(jobId && showShareButton);

  return (
    <TitleContainer>
      <Stack sx={{ minWidth: 0, pr: reserveButtonSpace ? "440px" : 0 }}>
        <Stack sx={{ minWidth: 0 }}>
          {jobId && <JobTagList tagIds={tagIds} createdAt={createdAt} />}
          <Stack direction="row" justifyContent="flex-start" alignItems="flex-start" spacing={2} sx={{ minWidth: 0 }}>
            <Typography variant="head20Bold" sx={{ minWidth: 0, wordBreak: "break-all", overflowWrap: "anywhere" }}>
              {jobTitle}
            </Typography>
            {jobId && (
              <Box sx={{ flexShrink: 0, mt: "4px" }}>
                <JobStatus />
              </Box>
            )}
          </Stack>
        </Stack>
        {jobId && (
          <Grid container gap={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body14" color={text.secondary} sx={{ display: "flex", alignItems: "center" }}>
                登録日:
                <span style={{ marginLeft: 4 }}>{createdAt ? dayjs(createdAt).format(DATE_FORMAT) : ""}</span>
              </Typography>
              |
              <Typography variant="body14" color={text.secondary} sx={{ display: "flex", alignItems: "center" }}>
                更新日:
                <span style={{ marginLeft: 4 }}>{updatedAt ? dayjs(updatedAt).format(DATE_FORMAT) : ""}</span>
              </Typography>
            </Box>
          </Grid>
        )}
      </Stack>
      {jobId && showShareButton && (
        <Stack direction="row" gap={2} sx={{ position: "absolute", top: 0, right: 0 }}>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleShareMenuOpen}
            sx={{
              width: "228px",
            }}
            startIcon={<IconSocialShare />}
            endIcon={<IconChevronDown />}
          >
            パートナーへURL共有
          </Button>
          <Button
            variant="secondary"
            size="sm"
            startIcon={<IconAssignment />}
            endIcon={<IconChevronDown />}
            onClick={handleExportMenuOpen}
            isLoading={isExporting}
          >
            求人票を出力
          </Button>
          <Menu anchorEl={exportMenuAnchorEl} open={Boolean(exportMenuAnchorEl)} onClose={handleExportMenuClose}>
            <MenuItem
              onClick={() => {
                handleExportMenuClose();
                handleExportDocx();
              }}
            >
              <ListItemText>
                <Typography variant="body14">求人票を出力</Typography>
              </ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleExportMenuClose();
                exportOptionsModal.open();
              }}
            >
              <ListItemText>
                <Typography variant="body14">求人票出力のオプション</Typography>
              </ListItemText>
              <ListItemIcon sx={{ minWidth: "auto", ml: 1 }}>
                <Tooltip title="" placement="bottom">
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <IconInfoOutline width={16} height={16} />
                  </Box>
                </Tooltip>
              </ListItemIcon>
            </MenuItem>
          </Menu>
          <Menu anchorEl={shareMenuAnchorEl} open={Boolean(shareMenuAnchorEl)} onClose={handleShareMenuClose}>
            <MenuItem onClick={() => handleShareOption("full")}>
              <ListItemText>
                <Typography variant="body14">全ての情報を共有</Typography>{" "}
              </ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleShareOption("masked")}>
              <ListItemText>
                <Typography variant="body14">一部情報をマスキングして共有</Typography>
              </ListItemText>
              <ListItemIcon sx={{ minWidth: "auto", ml: 1 }}>
                <Tooltip title="企業名を非表示にして募集条件のみを表示します" placement="bottom">
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <IconInfoOutline width={16} height={16} />
                  </Box>
                </Tooltip>
              </ListItemIcon>
            </MenuItem>
          </Menu>
        </Stack>
      )}

      <ShareEnterpriseURLModal
        key={shareType}
        modal={shareModal}
        title={shareType === "full" ? "パートナーへ求人情報を共有（全て）" : "パートナーへの求人情報の共有（一部マスキング）"}
        description="パートナーに共有するURL、パスワードをこちらから発行できます。"
        entityType="job"
        entityId={jobId}
        shareType={shareType === "full" ? SHARE_TYPE.FULL : SHARE_TYPE.MASKED}
        initialShareUrl={currentShareLink?.shareUrl ?? undefined}
        initialPassword={currentShareLink?.sharePassword ?? undefined}
        initialShareUrlExpiredAt={currentShareLink?.shareUrlExpiredAt ?? undefined}
        onGenerateSuccess={onShareGenerated}
      />

      <JobExportOptionsModal modal={exportOptionsModal} jobId={jobId} jobTitle={title} />
    </TitleContainer>
  );
};

const TitleContainer = styled(Stack)`
  width: ${JOB_WIDTH_FULL}px;
  position: relative;
  margin-bottom: 8px;
`;
